import {
  parseVisualGameAdminUserActionResponse,
  parseVisualGameAdminUserBehaviorTimelineResponse,
  parseVisualGameAdminUserCreditsAdjustRequest,
  parseVisualGameAdminUserDetailResponse,
  parseVisualGameAdminUserListResponse,
  parseVisualGameAdminUserProvisionRequest,
  parseVisualGameAdminUserProfileUpdateRequest,
  parseVisualGameAdminUserSubscriptionUpdateRequest,
  parseVisualGameAdminUserSummary,
} from '@drama/contracts'

import { createCallchainHttp } from './callchain-http.js'

const USER_QUERY_KEYS = new Set([
  'q',
  'account_type',
  'subscription_status',
  'limit',
  'cursor',
  'since',
  'until',
])

let http = createUnconfiguredHttp()

/** @param {Parameters<typeof createCallchainHttp>[0]} opts */
export function configureAdminUsersHttp(opts) {
  http = createCallchainHttp({ ...opts, basePath: '/api' })
}

/** @callchain.routeId visualGame.users.summary */
export async function getUserSummary() {
  const response = await http.get('/admin/visual-game/users/summary', {
    routeId: 'visualGame.users.summary',
  })
  return parseVisualGameAdminUserSummary(response)
}

/**
 * @callchain.routeId visualGame.users.list
 * @param {Record<string, string|number|null|undefined>} query
 */
export async function listUsers(query) {
  assertAllowedQuery(query)
  const response = await http.get('/admin/visual-game/users', {
    query,
    routeId: 'visualGame.users.list',
  })
  return parseVisualGameAdminUserListResponse(response)
}

/**
 * @callchain.routeId visualGame.users.provision
 * @param {Record<string, unknown>} input
 */
export async function provisionUser(input) {
  const body = parseVisualGameAdminUserProvisionRequest(input)
  const response = await http.post('/admin/visual-game/users', {
    body,
    routeId: 'visualGame.users.provision',
  })
  return parseVisualGameAdminUserActionResponse(response)
}

/**
 * @callchain.routeId visualGame.users.detail
 * @param {string} userId
 */
export async function getUserDetail(userId) {
  const response = await http.get(`/admin/visual-game/users/${encodeURIComponent(userId)}`, {
    routeId: 'visualGame.users.detail',
  })
  return parseVisualGameAdminUserDetailResponse(response)
}

export async function getUserBehaviorEvents(userId) {
  const response = await http.get(`/admin/visual-game/users/${encodeURIComponent(userId)}/behavior-events`, {
    routeId: 'visualGame.users.behaviorEvents',
  })
  return parseVisualGameAdminUserBehaviorTimelineResponse(response)
}

/**
 * @callchain.routeId visualGame.users.profile.update
 * @param {string} userId
 * @param {Record<string, unknown>} input
 */
export async function updateUserProfile(userId, input) {
  const body = parseVisualGameAdminUserProfileUpdateRequest(input)
  const response = await http.post(`/admin/visual-game/users/${encodeURIComponent(userId)}/profile`, {
    body,
    routeId: 'visualGame.users.profile.update',
  })
  return parseVisualGameAdminUserActionResponse(response)
}

/**
 * @callchain.routeId visualGame.users.credits.adjust
 * @param {string} userId
 * @param {Record<string, unknown>} input
 */
export async function adjustUserCredits(userId, input) {
  const body = parseVisualGameAdminUserCreditsAdjustRequest(input)
  const response = await http.post(`/admin/visual-game/users/${encodeURIComponent(userId)}/credits`, {
    body,
    routeId: 'visualGame.users.credits.adjust',
  })
  return parseVisualGameAdminUserActionResponse(response)
}

/**
 * @callchain.routeId visualGame.users.subscription.update
 * @param {string} userId
 * @param {Record<string, unknown>} input
 */
export async function updateUserSubscription(userId, input) {
  const body = parseVisualGameAdminUserSubscriptionUpdateRequest(input)
  const response = await http.post(`/admin/visual-game/users/${encodeURIComponent(userId)}/subscription`, {
    body,
    routeId: 'visualGame.users.subscription.update',
  })
  return parseVisualGameAdminUserActionResponse(response)
}

/** @param {Record<string, unknown>} query */
function assertAllowedQuery(query) {
  for (const key of Object.keys(query)) {
    if (!USER_QUERY_KEYS.has(key)) {
      throw new Error(`Unknown users query key: ${key}`)
    }
  }
}

function createUnconfiguredHttp() {
  return {
    get() {
      throw new Error('configureAdminUsersHttp must be called before users API use')
    },
    post() {
      throw new Error('configureAdminUsersHttp must be called before users API use')
    },
  }
}
